import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setTokenGetter } from '../api/client';
import { impersonationApi } from '../api/impersonation';

interface User {
  userId?: number;
  personId: number;
  name: string;
  email: string;
  role: 'superAdmin' | 'organizationAdmin' | 'viewer';
  organizationId?: number;
  organizationIds?: number[];
  permissions?: string[];
  hasDirectoryRecord?: boolean;
}

interface AuthContextType {
  currentUser: User | null;
  // returnTo: the app path to open after sign-in (defaults to the current page)
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
  getAccessToken: () => Promise<string | null>;
  error: Error | null;
  authError: string | null;
  // Convenience properties derived from currentUser
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  organizationIds: number[];
  // Impersonation
  isImpersonating: boolean;
  realUser: User | null;
  startImpersonation: (userId: number) => Promise<void>;
  endImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// API base URL
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
    error: auth0Error,
  } = useAuth0();

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [realUser, setRealUser] = useState<User | null>(null);

  // Fetch user info from backend after Auth0 authentication
  const fetchUserInfo = useCallback(async () => {
    // Keep loading if Auth0 is still processing (including callback code exchange)
    if (auth0IsLoading) {
      setIsLoading(true);
      return;
    }

    // Not authenticated - stop loading, no user
    if (!auth0IsAuthenticated) {
      setCurrentUser(null);
      setIsLoading(false);
      return;
    }

    // Authenticated - fetch user info from backend
    setIsLoading(true);
    setAuthError(null);

    try {
      // Get access token from Auth0
      const token = await getAccessTokenSilently();

      // Build headers — include impersonation header if active in localStorage
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };
      const storedImpersonationId = localStorage.getItem('impersonation_user_id');
      if (storedImpersonationId) {
        headers['X-Impersonate-User-Id'] = storedImpersonationId;
      }

      // Call backend to get user info (this also triggers JIT provisioning if needed)
      const response = await fetch(`${API_URL}/auth/me`, { headers });

      if (response.ok) {
        const userData = await response.json();
        setCurrentUser({
          userId: userData.userId,
          personId: userData.personId,
          name: userData.name || auth0User?.name || 'User',
          email: userData.email || auth0User?.email || '',
          role: userData.role || 'viewer',
          organizationId: userData.organizationId,
          organizationIds: userData.organizationIds || [],
          permissions: userData.permissions || [],
          hasDirectoryRecord: userData.hasDirectoryRecord || false,
        });

        // Handle impersonation state from backend response
        if (userData.isImpersonating && userData.impersonator) {
          setIsImpersonating(true);
          setRealUser({
            userId: userData.impersonator.userId,
            personId: userData.impersonator.personId,
            name: userData.impersonator.name,
            email: '',
            role: 'superAdmin',
          });
        } else {
          setIsImpersonating(false);
          setRealUser(null);
          // Clean up stale localStorage if backend says not impersonating
          if (storedImpersonationId) {
            localStorage.removeItem('impersonation_user_id');
          }
        }
      } else if (response.status === 403) {
        // Check for domain_not_allowed error
        const errorHeader = response.headers.get('X-Auth-Error');
        const errorData = await response.json();

        if (errorHeader === 'domain_not_allowed') {
          setAuthError('domain_not_allowed');
        } else {
          setAuthError(errorData.detail || 'Access denied');
        }
        setCurrentUser(null);
      } else if (response.status === 401) {
        // Token invalid or expired
        setCurrentUser(null);
      } else {
        console.error('Failed to fetch user info:', response.status);
        setCurrentUser(null);
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      setCurrentUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [auth0IsAuthenticated, auth0IsLoading, getAccessTokenSilently, auth0User]);

  // Fetch user info when Auth0 authentication state changes
  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  // Register token getter with API client
  useEffect(() => {
    setTokenGetter(async () => {
      if (!auth0IsAuthenticated) {
        return null;
      }
      try {
        return await getAccessTokenSilently();
      } catch (error) {
        console.error('Error getting access token for API:', error);
        return null;
      }
    });
  }, [auth0IsAuthenticated, getAccessTokenSilently]);

  const login = async (returnTo?: string) => {
    setAuthError(null);
    await loginWithRedirect({
      appState: { returnTo: returnTo || `${window.location.pathname}${window.location.search}` },
    });
  };

  const logout = async () => {
    // Clear impersonation on logout
    localStorage.removeItem('impersonation_user_id');
    setIsImpersonating(false);
    setRealUser(null);
    setCurrentUser(null);
    setAuthError(null);
    await auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (!auth0IsAuthenticated) {
      return null;
    }
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  };

  const startImpersonation = async (userId: number) => {
    // Call backend to validate and log
    await impersonationApi.start(userId);

    // Store in localStorage so header is attached to all subsequent requests
    localStorage.setItem('impersonation_user_id', String(userId));

    // Save the real superAdmin user
    setRealUser(currentUser);

    // Re-fetch user info — will now return impersonated user data
    await fetchUserInfo();
  };

  const endImpersonation = async () => {
    // Clear localStorage FIRST so the endImpersonation call goes without the header
    localStorage.removeItem('impersonation_user_id');

    // Call backend to log the end (without impersonation header)
    try {
      await impersonationApi.end();
    } catch (error) {
      console.error('Error logging impersonation end:', error);
    }

    // Restore state
    setIsImpersonating(false);
    setRealUser(null);

    // Re-fetch user info — will now return real superAdmin data
    await fetchUserInfo();
  };

  // Compute convenience properties from currentUser
  const isSuperAdmin = currentUser?.role === 'superAdmin';
  const isOrgAdmin = currentUser?.role === 'organizationAdmin';
  const organizationIdsValue = currentUser?.organizationIds || [];

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        login,
        logout,
        isLoading,
        isAuthenticated: auth0IsAuthenticated && currentUser !== null,
        getAccessToken,
        error: auth0Error || null,
        authError,
        // Convenience properties
        isSuperAdmin,
        isOrgAdmin,
        organizationIds: organizationIdsValue,
        // Impersonation
        isImpersonating,
        realUser,
        startImpersonation,
        endImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Hook for making authenticated API calls
export const useAuthenticatedFetch = () => {
  const { getAccessToken } = useAuth();

  const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const token = await getAccessToken();

    const headers = new Headers(options.headers);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  return authFetch;
};
